import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How our engineering team got 12x faster using these 5 lessons about integrating 3rd-party services",
  "description": "",
  "published": "2018-03-15T00:00:00.000Z",
  "redirect_from": ["/blog/engineering-team-got-faster-lessons-3rd-party-services/swizec/8283"],
  "categories": "Front End, Learning, Technical, Thoughts",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "/4fd0282d6a7a4da9cefc4541243339e2/media-mYqaRkXyoGbcY-giphy.gif",
        "alt": "Rube goldberg machine"
      }}></img></p>
    <p>{`At `}<a parentName="p" {...{
        "href": "https://www.yup.com"
      }}>{`Yup`}</a>{`, we've been integrating a lot of 3rd-party services lately to help our sales team. First, we added `}<a parentName="p" {...{
        "href": "https://getbase.com/"
      }}>{`BaseCRM`}</a>{` to the mix to help us manage leads, then we added `}<a parentName="p" {...{
        "href": "https://www.activecampaign.com/"
      }}>{`ActiveCampaign`}</a>{` to help with email campaigns. A while ago, we integrated `}<a parentName="p" {...{
        "href": "https://mixpanel.com/"
      }}>{`Mixpanel`}</a>{`.`}</p>
    <p>{`It took us 6 months to discover our Mixpanel integration had problems. Scaling hurt. `}<em parentName="p">{`Small`}</em>{` scaling that is.`}</p>
    <p>{`It took us 3 months to get BaseCRM to work reliably.`}</p>
    <p>{`ActiveCampaign took just 1 week and it’s working great. 👌`}</p>
    <p><strong parentName="p">{`That's a 12x improvement in engineering team performance.`}</strong></p>
    <p><img parentName="p" {...{
        "src": "/35a6fe4955a1316163da2da6b9ca5c79/media-CDJo4EgHwbaPS-giphy.gif",
        "alt": null
      }}></img></p>
    <p>{`You might think ActiveCampaign is much easier to integrate. Or maybe that it's more reliable and has better documentation. Mixpanel, by far, has the best documentation of all three. BaseCRM… ugh.`}</p>
    <p>{`Really, it's because we learned a couple painful lessons 👇`}</p>
    <h2 {...{
      "id": "what-makes-3rd-party-hard"
    }}>{`What makes 3rd party hard`}</h2>
    <p>{`Adding a 3rd party service is easy in theory. Every service out there claims to be one and done.`}</p>
    <p>{`Copy this code. Plop it in your app. You're done. Hooray `}{`\\`}{`o/`}</p>
    <p>{`Stripe famously takes just 7 lines of code to integrate. Mixpanel says you can do it with just a few function calls.`}</p>
    <p>{`Basic integrations are easy. But most basic integrations take control away from you and your team and hand it over to the 3rd party service’s preferred setup.`}</p>
    <p><strong parentName="p">{`But 👇`}</strong></p>
    <p>{`When something goes wrong, how do you debug it? You don't.`}</p>
    <p>{`When the service errors out, what do you do? You crash your app.`}</p>
    <p>{`When your business folks want to get metrics, how do they do it? They use ten different dashboards for ten different services and hope for the best.`}</p>
    <p>{`That's no way to build a unicorn. That's how you build a sidehustle.`}</p>
    <h2 {...{
      "id": "track-your-own-stuff"
    }}>{`Track your own stuff`}</h2>
    <blockquote>
      <p parentName="blockquote">{`How are we going to know when something is wrong? How do we know what's wrong?`}</p>
    </blockquote>
    <p>{`Every 3rd party integration can go wrong. Services fall down, networks get crappy, traffic gets spikey.`}</p>
    <p>{`Hell, you could just forget to fill up your credit card and get your access revoked. Things happen ¯`}<em parentName="p">{`(ツ)`}</em>{`/¯`}</p>
    <p>{`When something does happen, it's important that you find out before your users do. Most services give you some ability to tell when something isn't working. You can go into their dashboards, see metrics, and make sure things are wiggling in the right direction.`}</p>
    <p>{`But that means someone has to remember to check.`}</p>
    <p><img parentName="p" {...{
        "src": "/ba502ebfbbe58abd49327cccd4497c7b/media-5fBH6zc5cLWMCWrISHK-giphy.gif",
        "alt": null
      }}></img></p>
    <p>{`Now, monitoring is a whole science of its own. If you have the funds, I warmly recommend getting a real monitoring expert to check your infrastructure and make suggestions. Read a book or three if you have time.`}</p>
    <p>{`In lieu of expert monitoring, adding rows to your database every time you ping a service is good enough. Store who you called, what you sent them, why you did it, when you did it, and build a chart that wiggles around when you add stuff.`}</p>
    <p>{`This helps you know things are working `}<em parentName="p">{`and`}</em>{` gives you enough information to debug issues later. You can even replay requests and see what happens.`}</p>
    <h2 {...{
      "id": "keep-your-own-data"
    }}>{`Keep your own data`}</h2>
    <p>{`Adding rows to the database for every service call brings us to the next lesson learned: Save your own data.`}</p>
    <p>{`3rd party services looooove to say you can rely on them, keep all of your data there, and live a carefree life with not a worry on your mind. This is great for them because the more data they have, the harder it is for you to leave. This is bad for you because the more data they have, the harder it is for you to leave.`}</p>
    <p>{`Storing your own data, even in a raw format, means you can pull the plug any time. Don't like a service anymore? Find an alternative and switch.`}</p>
    <p>{`You can import your old data (or not). At least you have it. No need to worry about whether a service offers data export. No need to fret about how useful or not useful that export.`}</p>
    <p>{`Services love to offer data export without giving you anything crazy useful. You won't find out until you try.`}</p>
    <p>{`Besides, if you keep all your data, you can run your own analytics, build a common dashboard for business metrics, correlate stuff across services, etc. Your product managers will love you.`}</p>
    <h2 {...{
      "id": "encapsulate--isolate-errors"
    }}>{`Encapsulate & isolate errors`}</h2>
    <p>{`You know all those places where you make a call to a service either through a library or directly? Each of those can fail.`}</p>
    <p>{`I wrote about `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/always-put-side-effects-last/swizec/8057"
      }}>{`building distributed transactions`}</a>{` before. Correctness across multiple services is `}<em parentName="p">{`really`}</em>{` hard to ensure.`}</p>
    <p>{`When a call does go wrong, it's important that it doesn't break the rest of the system.`}</p>
    <p>{`A common problem is when you decide to process a bunch of entries in a loop.`}</p>
    <pre><code parentName="pre" {...{}}>{`for entry in queue
    thirdParty(entry)
end
`}</code></pre>
    <p>{`If that `}<inlineCode parentName="p">{`thirdParty`}</inlineCode>{` call fails, the queue stops processing. When you try again, it fails at the same spot. Your queue never processes, and it keeps growing.`}</p>
    <p>{`Ouch.`}</p>
    <p>{`You can wrap each call in a `}<inlineCode parentName="p">{`try/catch`}</inlineCode>{` statement for your language. Skip over the failures, process everything else, and make a note in a database that the particular entry failed.`}</p>
    <p>{`Much better.`}</p>
    <p>{`An even better fix I've found is to make as many calls as possible in isolated background workers of their own. Tiny workers with one per 3rd party service call.`}</p>
    <p>{`It gives you error isolation, only 1 call per worker, and an easy way to retry calls. Most queue systems offer that, `}<em parentName="p">{`and`}</em>{` you ensure entries don't have to wait for each other. This makes your system fast.`}</p>
    <h2 {...{
      "id": "eventual-consistency-is-your-friend"
    }}>{`Eventual consistency is your friend`}</h2>
    <p>{`The background worker approach to error isolation and call retrying brings us to the last lesson we learned: `}<strong parentName="p">{`Eventual consistency is your friend`}</strong>{`.`}</p>
    <p>{`Eventual consistency was all the rage a few years ago when Cassandra and other NoSQL databases topped the charts of internet hype. Those continue to be great ideas. Your team is probably too small to make good use of them. Ours definitely is.`}</p>
    <p>{`You can use eventual consistency even if you're in Postgres. Process stuff in workers.`}</p>
    <p>{`When a request comes in that involves a 3rd party service (or a webhook `}<em parentName="p">{`from`}</em>{` a service), store the raw payload in your database and have a background worker checking every few minutes to process those requests. Most things don't have to happen inbound.`}</p>
    <p>{`When you're sending things to a service, put them in the database instead and send them later. Then update whatever needs to update as a result of that call.`}</p>
    <p>{`It makes your system harder to test and understand, but it's totally worth it. Would you rather have a more Rube-Goldbergian architecture or hotpatch a bug in production at 3am when everything falls apart and users are crying?`}</p>
    <p><img parentName="p" {...{
        "src": "/7e1ad49eee3eab735a44bffc49fbd2e4/media-TfItZ1HqJCTza-giphy.gif",
        "alt": null
      }}></img></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      